/**
 * Register map for DALY Low-Voltage BMS protocol
 */

import {
  parseDalyChargeDischargeStatus,
  parseDalyFault1,
  parseDalyFault2,
  parseDalyFault3,
  parseDalyFault4,
} from './dalyParser';

const DALY_REGISTER_MAP = {
  0x38: { name: 'Total Voltage', unit: 'V', multiplier: 0.1, signed: false, decimals: 1 },
  0x39: { name: 'Current', unit: 'A', multiplier: 0.1, signed: false, offset: 30000, decimals: 1 },
  0x3A: { name: 'SOC', unit: '%', multiplier: 0.1, signed: false, decimals: 1 },
  0x3B: { name: 'LIFE Heartbeat', unit: '', multiplier: 1, signed: false },
  0x3C: { name: 'Battery Quantity', unit: '', multiplier: 1, signed: false },
  0x3D: { name: 'Temp Sensor Quantity', unit: '', multiplier: 1, signed: false },
  0x3E: { name: 'Max Cell Voltage', unit: 'mV', multiplier: 1, signed: false },
  0x3F: { name: 'Max Cell Voltage Index', unit: '', multiplier: 1, signed: false },
  0x40: { name: 'Min Cell Voltage', unit: 'mV', multiplier: 1, signed: false },
  0x41: { name: 'Min Cell Voltage Index', unit: '', multiplier: 1, signed: false },
  0x42: { name: 'Cell Voltage Difference', unit: 'mV', multiplier: 1, signed: false },
  0x43: { name: 'Max Temperature', unit: '°C', multiplier: 1, signed: false, offset: 40 },
  0x44: { name: 'Max Temperature Index', unit: '', multiplier: 1, signed: false },
  0x45: { name: 'Min Temperature', unit: '°C', multiplier: 1, signed: false, offset: 40 },
  0x46: { name: 'Min Temperature Index', unit: '', multiplier: 1, signed: false },
  0x47: { name: 'Temperature Difference', unit: '°C', multiplier: 1, signed: false },
  0x48: { name: 'Charge/Discharge Status', unit: '', parser: parseDalyChargeDischargeStatus },
  0x49: { name: 'Charger Detected', unit: '', multiplier: 1, signed: false },
  0x4A: { name: 'Load Detected', unit: '', multiplier: 1, signed: false },
  0x4B: { name: 'Remaining Capacity', unit: 'Ah', multiplier: 0.1, signed: false, decimals: 1 },
  0x4C: { name: 'Cycle Times', unit: '', multiplier: 1, signed: false },
  0x4D: { name: 'Balance State', unit: '', multiplier: 1, signed: false },
  0x4E: { name: 'Balance Current', unit: 'A', multiplier: 0.1, signed: false, decimals: 1 },
  0x52: { name: 'Charge MOS State', unit: '', multiplier: 1, signed: false },
  0x53: { name: 'Discharge MOS State', unit: '', multiplier: 1, signed: false },
  0x54: { name: 'Precharge MOS State', unit: '', multiplier: 1, signed: false },
  0x55: { name: 'Heating MOS State', unit: '', multiplier: 1, signed: false },
  0x56: { name: 'Fan MOS State', unit: '', multiplier: 1, signed: false },
  0x57: { name: 'Average Voltage', unit: 'mV', multiplier: 1, signed: false },
  0x58: { name: 'Power', unit: 'W', multiplier: 1, signed: true },
  0x59: { name: 'Energy', unit: 'Wh', multiplier: 1, signed: false },
  0x5A: { name: 'MOS Temperature', unit: '°C', multiplier: 1, signed: false, offset: 40 },
  0x5B: { name: 'Ambient Temperature', unit: '°C', multiplier: 1, signed: false, offset: 40 },
  0x5C: { name: 'Heating Temperature', unit: '°C', multiplier: 1, signed: false, offset: 40 },
  0x66: { name: 'Fault Status 1', unit: '', parser: parseDalyFault1 },
  0x67: { name: 'Fault Status 2', unit: '', parser: parseDalyFault2 },
  0x68: { name: 'Fault Status 3', unit: '', parser: parseDalyFault3 },
  0x69: { name: 'Fault Status 4', unit: '', parser: parseDalyFault4 },
  0x121: { name: 'Charge MOS Control', unit: '', multiplier: 1, signed: false },
  0x122: { name: 'Discharge MOS Control', unit: '', multiplier: 1, signed: false },
};

/**
 * Get register information for DALYLV BMS protocol
 * @param {number} address - Register address
 * @returns {object} Register descriptor { name, unit, multiplier, signed, offset?, decimals?, parser? }
 */
export function getDalyRegisterInfo(address) {
  // Cell voltages: 0x00-0x2F (1-48)
  if (address >= 0x00 && address <= 0x2F) {
    return {
      name: `Cell ${address + 1} Voltage`,
      unit: 'mV',
      multiplier: 1,
      signed: false,
    };
  }

  // Temperatures: 0x30-0x37 (1-8)
  if (address >= 0x30 && address <= 0x37) {
    return {
      name: `Temperature ${address - 0x30 + 1}`,
      unit: '°C',
      multiplier: 1,
      signed: false,
      offset: 40,
    };
  }

  return (
    DALY_REGISTER_MAP[address] || {
      name: `Register 0x${address.toString(16).toUpperCase()}`,
      unit: '',
      multiplier: 1,
      signed: false,
    }
  );
}
